import React, { useEffect, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useStore } from '@tanstack/react-form';
import { XMarkIcon } from '@heroicons/react/24/outline';
import type { PositionDetail } from '@/types';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { BeforeAfterConfirm } from '@/components/ui/BeforeAfterConfirm';
import { useToastStore } from '@/stores';
// Specific module, NOT the hooks barrel — keeps modal unit tests mockable (matches the
// retired AmendModal).
import { canPerform } from '@/features/trades/hooks/trade-actions';
import type { useUpdatePosition } from '@/hooks/trades';
import { getFxRateInlineWarning } from '@/services/trades/trade-validation';
import { showsClosingCash } from '@/utils/trades/closing-cash';
import { amendChangeRows, diffAmendPayload } from '@/services/trades/amend-mapper';
import { useAmendTradeForm } from '../../../hooks/useAmendTradeForm';
import {
  SecurityDetailsSection,
  AccountDetailsSection,
  PositionDetailsSection,
  NonCashCollateralSection,
  RateDetailsSection,
  PositionDatesSection,
  SettlementDetailsSection,
  TradeSummarySection,
  labelClassName,
  readOnlyInputClassName,
} from '../../trade-form-sections';

const { Panel: DialogPanel, Title: DialogTitle } = Dialog;
const { Child: TransitionChild } = Transition;

interface AmendTradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  trade: PositionDetail;
  /** The `useUpdatePosition` mutation handle, owned by the hosting page. */
  updatePosition: ReturnType<typeof useUpdatePosition>;
}

/** Static (always-expanded) section wrapper — amend shows the whole booking form,
 *  so the create form's collapse affordance is dropped for a simpler review. */
const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div>
    <div className="w-full py-3 px-4 bg-gray-50 border-b border-gray-200">
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
    </div>
    {children}
  </div>
);

/**
 * LR-484: amend a PENDING trade's NUMBERS by reusing the SAME trade-entry form the
 * trader booked with — every condition field is locked (amendMode), only the agreed
 * numbers are editable. "Change trade" opens a before -> after approval (changed fields
 * only) + optional comment; confirm PATCHes the diff. Supersedes the quantity-only
 * AmendModal. The create form (useCreateTradeForm / CreateTradeModal) is untouched.
 */
export function AmendTradeModal({ isOpen, onClose, trade, updatePosition }: AmendTradeModalProps) {
  const amend = useAmendTradeForm({ trade, isOpen });
  const { form } = amend;
  const { mutate, isPending, error, reset } = updatePosition;
  const { addToast } = useToastStore();

  const [approvalOpen, setApprovalOpen] = useState(false);
  const [comment, setComment] = useState('');

  // Reactive view of the edits → the approval rows + the PATCH body.
  const values = useStore(form.store, (s) => s.values);
  const rows = amendChangeRows(trade, values);
  const payload = diffAmendPayload(trade, values);
  const hasChanges = Object.keys(payload).length > 0;
  // kvi review #1: amend is pending-only. If the trade left `pending` in the
  // background after the connector fetched it, gate the affordances too (not just
  // handleConfirm) so the operator never reaches a Confirm that silently no-ops.
  const canAmend = canPerform(trade, 'amend');

  // Reset transient modal state strictly on the closed→open transition (the form
  // itself reseeds inside useAmendTradeForm with the same edge-guard).
  const wasOpen = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      setApprovalOpen(false);
      setComment('');
      reset();
    }
    wasOpen.current = isOpen;
  }, [isOpen, trade.id, reset]);

  const handleConfirm = () => {
    // Guard the full path: amend is pending-only and a no-op submit must not fire.
    if (!canAmend || !hasChanges) return;
    // LR-484: thread the optional approval comment into the PATCH. The serializer
    // takes it as a write-only field and stamps it onto each PositionHistory row
    // (→ surfaced on the /audit/ "amended" event). Omit when blank.
    const note = comment.trim();
    mutate(
      { id: trade.id, payload: note ? { ...payload, comment: note } : payload },
      {
        onSuccess: () => {
          addToast('success', `Trade #${trade.id} amended`);
          setApprovalOpen(false);
          onClose();
        },
        // kvi review #2: a failure is surfaced ONCE — inline inside the still-open
        // ConfirmDialog (error={error} below). No onError toast, so the operator
        // doesn't see the same error twice.
      },
    );
  };

  const fxRateWarning = getFxRateInlineWarning(amend.fxRate);

  return (
    <>
      {isOpen && (
        <Transition show={isOpen} as={React.Fragment}>
          <Dialog as="div" className="relative z-[9999]" onClose={() => {}} static>
            <TransitionChild
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black bg-opacity-25 z-[9999]" />
            </TransitionChild>
            <div className="fixed inset-0 overflow-y-auto z-[10000]">
              <div className="flex min-h-full items-start justify-center py-5">
                <TransitionChild
                  as={React.Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <DialogPanel className="relative w-full max-w-7xl max-h-[calc(100vh-40px)] transform overflow-hidden rounded-lg bg-white shadow-xl transition-all flex flex-col z-[10001]">
                    <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                      <DialogTitle className="text-lg font-semibold text-gray-900">
                        Amend Trade
                      </DialogTitle>
                      <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-500 focus:outline-none"
                        aria-label="Close"
                      >
                        <XMarkIcon className="h-6 w-6" />
                      </button>
                    </div>

                    <div className="flex-1 overflow-y-auto">
                      {/* amend-trade-form: scopes the "locked field" greying (index.css) so
                          every disabled condition LOOKS locked — only the editable numbers
                          stay white/active. */}
                      <div className="amend-trade-form divide-y divide-gray-200">
                        {/* Security — locked; the create-form search is replaced by a
                            read-only display (security is a condition, not amendable). */}
                        <div className="p-6">
                          <label className={labelClassName}>Security</label>
                          <input
                            type="text"
                            readOnly
                            value={`${trade.symbol_id}${trade.description ? ` — ${trade.description}` : ''}`}
                            className={readOnlyInputClassName}
                          />
                          <p className="mt-1 text-[11px] text-gray-500">
                            Amend trade <strong>#{trade.id}</strong> before it settles. Only the
                            deal&apos;s numbers can change; its conditions are locked.
                          </p>
                        </div>

                        <Section title="Security Details">
                          <SecurityDetailsSection security={amend.securityDisplay} />
                        </Section>

                        <Section title="Account Details">
                          <AccountDetailsSection
                            form={form}
                            isSubmitted={false}
                            selectedInventoryItem={null}
                            counterparties={amend.counterparties}
                            custodians={amend.custodians}
                            amendMode
                          />
                        </Section>

                        <Section title="Position Details">
                          <PositionDetailsSection
                            form={form}
                            isSubmitted={false}
                            selectedInventoryItem={null}
                            securityCurrency={amend.securityCurrency}
                            calculatedValues={amend.calculatedValues}
                            availableTradeTypes={amend.availableTradeTypes}
                            tradeTypeLabels={amend.tradeTypeLabels}
                            amendMode
                          />
                        </Section>

                        {amend.collateralType === 'non_cash' && (
                          <Section title="Non-cash Collateral Details">
                            <NonCashCollateralSection
                              form={form}
                              isSubmitted={false}
                              custodians={amend.custodians}
                              collateralQuantity={amend.collateralQuantity}
                              collateralCurrentMarketPrice={amend.collateralCurrentMarketPrice}
                              collateralPercent={amend.collateralPercent}
                              collateralSettlementPlaceName={trade.collateral_settlement_place_name}
                              amendMode
                            />
                          </Section>
                        )}

                        <Section title="Rate Details">
                          <RateDetailsSection
                            form={form}
                            isSubmitted={false}
                            selectedInventoryItem={null}
                            rateType={amend.rateType}
                            rateEntryMode={amend.rateEntryMode}
                            securityCurrency={amend.securityCurrency}
                            isCrossCurrency={amend.isCrossCurrency}
                            effectiveSettlementCurrency={amend.effectiveSettlementCurrency}
                            totalRate={amend.totalRate}
                            calculatedValues={amend.calculatedValues}
                            benchmarkRates={amend.benchmarkRates}
                            benchmarkMeta={amend.benchmarkMeta}
                            amendMode
                          />
                        </Section>

                        <Section title="Position Dates">
                          <PositionDatesSection
                            form={form}
                            isSubmitted={false}
                            isFormDisabled={amend.isFormDisabled}
                            tradeDuration={amend.tradeDuration}
                            settlementDate={amend.settlementDate}
                            selectedInventoryItem={null}
                            securityCurrency={amend.securityCurrency}
                            calculatedValues={amend.calculatedValues}
                            tradeDateHoliday={amend.tradeDateHoliday}
                            settlementDateHoliday={amend.settlementDateHoliday}
                            amendMode
                          />
                        </Section>

                        <Section title="Settlement Details">
                          <SettlementDetailsSection
                            form={form}
                            isSubmitted={false}
                            isFormDisabled={amend.isFormDisabled}
                            securityCurrency={amend.securityCurrency}
                            effectiveSettlementCurrency={amend.effectiveSettlementCurrency}
                            isCrossCurrency={amend.isCrossCurrency}
                            isCashMatchingMode={amend.isCashMatchingMode}
                            isUsingFallbackRates={amend.isUsingFallbackRates}
                            deliveryType={amend.deliveryType}
                            settlementVenue={amend.settlementVenue}
                            counterpartyName={amend.counterpartyName}
                            calculatedValues={amend.calculatedValues}
                            fxRateWarning={fxRateWarning}
                            securitiesSettlementPlaceName={trade.securities_settlement_place_name}
                            cashSettlementPlaceName={trade.cash_settlement_place_name}
                            amendMode
                          />
                        </Section>

                        <Section title="Trade Summary">
                          <TradeSummarySection
                            securityCurrency={amend.securityCurrency}
                            effectiveSettlementCurrency={amend.effectiveSettlementCurrency}
                            isCrossCurrency={amend.isCrossCurrency}
                            calculatedValues={amend.calculatedValues}
                            showClosingSettlement={showsClosingCash(trade)}
                          />
                        </Section>

                        {/* Reason for the amendment. Lives HERE in the single form focus
                            context, NOT inside the approval ConfirmDialog — a textarea in
                            that stacked headlessui dialog can't hold focus (the form
                            dialog's focus-trap steals it back), so it was untypeable. The
                            approval step echoes this read-only before confirm. Mirrors how
                            CreateTradeModal keeps its Comments field in the form body. */}
                        <div className="p-6">
                          <label
                            htmlFor="amend-comment"
                            className="block text-[11px] font-medium uppercase tracking-wide text-gray-500 mb-1"
                          >
                            Reason for amendment (optional)
                          </label>
                          <textarea
                            id="amend-comment"
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            rows={2}
                            className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                            placeholder="Reason for the amendment…"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
                      <Button type="button" variant="secondary" onClick={onClose} disabled={isPending}>
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        variant="primary"
                        disabled={!hasChanges || isPending || !canAmend}
                        onClick={() => setApprovalOpen(true)}
                      >
                        Change trade
                      </Button>
                    </div>
                  </DialogPanel>
                </TransitionChild>
              </div>
            </div>
          </Dialog>
        </Transition>
      )}

      {/* Approval — before → after of the changed fields + optional comment. */}
      <ConfirmDialog
        show={approvalOpen}
        onClose={() => setApprovalOpen(false)}
        title="Confirm trade amendment"
        confirmLabel="Confirm changes"
        isLoading={isPending}
        onConfirm={handleConfirm}
        confirmDisabled={!hasChanges || !canAmend}
        error={error}
        errorFallback="Failed to amend trade"
      >
        <div className="space-y-3">
          <p className="text-sm text-gray-600">
            Review the changes to trade <strong>#{trade.id}</strong> ({trade.symbol_id}):
          </p>
          <BeforeAfterConfirm rows={rows} id="amend-change-preview">
            {comment.trim().length > 0 && (
              <div className="mt-3">
                <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500 mb-1">
                  Reason
                </p>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{comment.trim()}</p>
              </div>
            )}
          </BeforeAfterConfirm>
        </div>
      </ConfirmDialog>
    </>
  );
}
